/**
 * Page-level properties for page_view, derived from the URL.
 * Shared by the head GA4 page_view (gtagSnippet) and the Segment page() call.
 * Property names are Segment names; GTM maps them to GA4 parameters.
 *
 * Framework-agnostic: no imports, no env. The snippet body must stay ES5
 * because it runs in <head> before any bundle.
 *
 * @see https://nutrafol.atlassian.net/browse/NT2-29911
 */

export const PAGE_VIEW_PROPS_GLOBAL = "__nutrafolGetPageViewProps";

/**
 * Inline <head> script defining window.__nutrafolGetPageViewProps(pathname, referrer).
 * Must be injected before the GA4 config snippet.
 *
 * @returns {string}
 */
export const getPageViewPropsSnippet = () => `
window.${PAGE_VIEW_PROPS_GLOBAL} = function (pathname, referrer) {
  pathname = pathname || location.pathname || "/";
  var segments = pathname.split("/").filter(Boolean);
  var has = function (s) { return segments.indexOf(s) > -1; };
  var firstOf = function (list) {
    for (var i = 0; i < segments.length; i++) {
      if (list.indexOf(segments[i]) > -1) return segments[i];
    }
    return null;
  };
  var isBrand = has("brand");
  var isMen = has("men");
  var isSkin = has("skin");
  var isBlog = has("blog");
  var isInfluencer = has("influencer") || has("tt");
  var isPartner = has("partner");
  var product_line = isBrand ? "neutral" : isMen ? "men" : "women";
  var marketing_type = null;
  if (isInfluencer) marketing_type = "influencer";
  else if (isPartner) marketing_type = "brand_partnership";
  var page_type = isBlog
    ? "blog-post"
    : firstOf(["product", "lp", "quiz", "partner"]) || (isInfluencer ? "partner" : "lp");
  var page_template = "default";
  if (has("product")) page_template = "pdp";
  else if (has("lp")) page_template = "lp";
  else if (has("quiz")) page_template = "quiz";
  else if (has("account")) page_template = "account-dashboard";
  else if (has("learn")) page_template = "editorial";
  var page_intent = "browse";
  if (has("product") || has("lp")) page_intent = "convert";
  else if (has("quiz")) page_intent = "compare";
  else if (has("login")) page_intent = "sign_up";
  else if (has("learn")) page_intent = "educate";
  return {
    business_line: isBrand ? "Brand" : isSkin ? "Skin" : "Hair",
    page_type: page_type,
    page_detail: firstOf(["core", "scalp", "kit", "booster", "quiz", "category"]),
    product_line: product_line,
    product_gender: product_line === "neutral" ? "All" : product_line === "men" ? "Men" : "Women",
    page_template: page_template,
    page_intent: page_intent,
    page_referrer: referrer == null ? document.referrer || "" : referrer,
    marketing_owner: null,
    marketing_type: marketing_type
  };
};
`;

/**
 * App-code accessor for the head-defined builder. Returns {} if the snippet
 * was not injected, so callers never throw.
 *
 * @param {string} [pathname]
 * @param {string} [referrer]
 * @returns {Record<string, string | null>}
 */
export const getPageViewProps = (pathname, referrer) => {
  if (typeof window === "undefined") return {};
  const fn = window[PAGE_VIEW_PROPS_GLOBAL];
  return typeof fn === "function" ? fn(pathname, referrer) : {};
};
